import cn from 'classnames'
import { useState } from 'react'

import { Translation } from 'cng-common/features/translations'
import { Dropdown } from 'core-components/library/dropdown'
import { BaseCrud } from 'core-components/types'

import { MDF_ACTIVITIES_TRANSLATION_KEYS } from '../../../../configs/translations'
import { IMdfActivity } from '../../../../model/types'

import { TopSuggestions } from './components/topSuggestions'

import styles from './LinkedInitiativeCell.module.scss'

const KEYS = MDF_ACTIVITIES_TRANSLATION_KEYS

interface ILinkedInitiativeCellProps {
  activity: IMdfActivity
  /**
   * CRUD source of available P360 key initiatives for the dropdown.
   */
  initiativesSource: BaseCrud<any>
  /**
   * Lookup of P360 key initiative id → name, used to resolve suggestion labels.
   */
  initiativesById: Map<number, string>
  /**
   * Resolved name of the currently linked initiative, if any.
   */
  linkedName?: string
  isSaving?: boolean
  isDisabled?: boolean
  onLink: (activity: IMdfActivity, keyInitiativeId: number | null) => void
  onCreateInitiative: (activity: IMdfActivity, value: string) => void
}

const LINK_PLACEHOLDER = 'Search initiative…'

export const LinkedInitiativeCell = ({
  activity,
  initiativesSource,
  initiativesById,
  isSaving,
  isDisabled,
  onLink,
  onCreateInitiative,
}: ILinkedInitiativeCellProps) => {
  const { keyInitiativeId, keyInitiativeLinkType, keyInitiativeMatchPercent } = activity
  const isLinked = keyInitiativeId !== null
  const isInteractionDisabled = isSaving || isDisabled

  const [hasResults, setHasResults] = useState(true)

  const handleCreateInitiative = (value: string) => {
    onCreateInitiative(activity, value)
  }

  return (
    // Stop row-click from bubbling to the table row so interacting with the
    // initiative select/clear/create here never opens the activity details modal.
    <div className={styles.cell} onClick={(e) => e.stopPropagation()}>
      {isLinked && (
        <div className={styles.header}>
          {keyInitiativeLinkType === 'Auto' && (
            <span className={cn(styles.badge, styles.suggestion, styles.auto)}>
              Auto{keyInitiativeMatchPercent !== null ? ` ${keyInitiativeMatchPercent}%` : ''}
            </span>
          )}
          {keyInitiativeLinkType === 'Manual' && (
            <span className={cn(styles.badge, styles.manual)}>Manual</span>
          )}
        </div>
      )}

      {!isLinked && (
        <TopSuggestions
          suggestions={activity.keyInitiativeSuggestions}
          initiativesById={initiativesById}
          isSaving={isInteractionDisabled}
          onSelect={(id) => onLink(activity, id)}
        />
      )}

      <Dropdown
        classes={{ controlClasses: { root: styles.dropdown } }}
        source={initiativesSource}
        caption="NameExt"
        value={keyInitiativeId ?? ''}
        placeholder={LINK_PLACEHOLDER}
        disabled={isInteractionDisabled}
        onDataLoad={(items) => setHasResults(items.length > 0)}
        allowCreate={!isInteractionDisabled && !hasResults}
        onCreate={handleCreateInitiative}
        createButtonTemplate={(data) => {
          return (
            <button className={cn(data.className, styles.createButton)} onClick={data.onClick}>
              <Translation text={KEYS.linkage.createNew} />
            </button>
          )
        }}
        onChange={(value) => {
          if (value) {
            onLink(activity, value === '' || value == null ? null : Number(value))
          }
        }}
        renderFunction={(item) => {
          return <span>{item.NameExt || `#${item.KeyInitiativeId}`}</span>
        }}
        endContent={
          keyInitiativeId && !isInteractionDisabled ? (
            <span className={styles.clear} onClick={() => onLink(activity, null)}>
              <i className="kbase kbase-close-circle" />
            </span>
          ) : undefined
        }
      />
    </div>
  )
}

LinkedInitiativeCell.displayName = 'LinkedInitiativeCell'
